import { useState } from "react";
import { Link } from "wouter";
import { useListAvatars } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { useInfiniteItems } from "@/hooks/useInfiniteItems";
import avatarSunset from "@assets/IMG_7101_1779819286654.jpeg";
import { useToast } from "@/hooks/use-toast";

const RARITY_COLORS: Record<string, string> = {
  common:    "text-gray-400 border-gray-400/30 bg-gray-400/10",
  rare:      "text-blue-400 border-blue-400/30 bg-blue-400/10",
  epic:      "text-purple-400 border-purple-400/30 bg-purple-400/10",
  legendary: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10",
};

const STYLES = [
  { value: "all",     label: "All Styles" },
  { value: "human",   label: "Human"      },
  { value: "cyborg",  label: "Cyborg"     },
  { value: "fantasy", label: "Fantasy"    },
  { value: "alien",   label: "Alien"      },
];

export default function Avatars() {
  const [style, setStyle] = useState<string>("all");
  const { toast } = useToast();

  const { data: avatars, isLoading } = useListAvatars({
    style: style !== "all" ? style : undefined,
  });

  const { visibleItems, hasMore, loadMore, total } = useInfiniteItems(avatars, 8);

  const handleEquip = (_id: number, owned: boolean = false) => {
    if (owned) {
      toast({ title: "Avatar Equipped", description: "Your digital identity has been updated." });
    } else {
      toast({ title: "Purchase Required", description: "You must own this avatar to equip it.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Hero */}
      <div className="relative h-64 md:h-[40vh] w-full overflow-hidden">
        <img src={avatarSunset} alt="Avatars Hero" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-4 drop-shadow-2xl">Digital Identity</h1>
          <p className="text-xl text-gray-300 max-w-2xl drop-shadow-md">Define your presence in the New World. From human to alien, choose how you want to be seen.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-8">
        {/* ── Style filter chips — horizontal scroll ── */}
        <div className="flex justify-center mb-10">
          <div className="overflow-x-auto pb-1 no-scrollbar w-full sm:w-auto">
            <div className="flex gap-2 w-max mx-auto px-1">
              {STYLES.map(s => (
                <button
                  key={s.value}
                  onClick={() => setStyle(s.value)}
                  className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                    style === s.value
                      ? "bg-primary/20 border-primary text-primary shadow-[0_0_12px_rgba(var(--primary),0.3)]"
                      : "bg-card/50 border-border text-muted-foreground hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`tab-avatar-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Grid ── */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="aspect-[3/4] bg-card/50 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {visibleItems.map((avatar, idx) => (
                <motion.div
                  key={avatar.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (idx % 8) * 0.05 }}
                >
                  <Card className="overflow-hidden border-border bg-card/20 hover:bg-card/60 hover:border-primary/50 transition-all duration-300 group">
                    <div className="relative aspect-[3/4] w-full bg-black">
                      <img src={avatar.previewUrl} alt={avatar.name} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />

                      {avatar.rarity && (
                        <div className={`absolute top-3 left-3 px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wider border ${RARITY_COLORS[avatar.rarity]}`}>
                          {avatar.rarity}
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm px-2 py-1 rounded text-xs font-mono border border-white/10">
                        {avatar.style}
                      </div>

                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity p-6">
                        <Button
                          onClick={() => handleEquip(avatar.id, avatar.owned)}
                          className={`w-full mb-3 ${avatar.owned ? "bg-primary hover:bg-primary/80" : "bg-white text-black hover:bg-white/80"}`}
                          data-testid={`btn-action-${avatar.id}`}
                        >
                          {avatar.owned ? "EQUIP NOW" : "PURCHASE"}
                        </Button>
                        <Link href={`/avatars/${avatar.id}`} className="w-full" data-testid={`btn-view-${avatar.id}`}>
                          <Button variant="outline" className="w-full border-white/20 hover:bg-white/10">
                            VIEW DETAILS
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <CardContent className="p-4 flex flex-col gap-2">
                      <h3 className="font-bold text-lg uppercase tracking-tight">{avatar.name}</h3>
                      <div className="flex justify-between items-center text-sm font-mono">
                        <span className="text-muted-foreground">{avatar.blockchain}</span>
                        {avatar.owned ? (
                          <span className="text-primary font-bold">OWNED</span>
                        ) : (
                          <span className="text-white font-bold">{avatar.price.toLocaleString()} {avatar.currency}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {avatars?.length === 0 && (
                <div className="col-span-full text-center py-24 text-muted-foreground">
                  <p className="text-xl">No avatars found for this style.</p>
                </div>
              )}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="border border-white/20 hover:border-primary hover:text-primary font-mono uppercase tracking-widest px-8 py-2 rounded text-sm transition-colors"
                >
                  Load More Avatars
                </button>
              )}
              {!hasMore && total > 0 && (
                <p className="text-xs text-muted-foreground font-mono">Showing all {total} avatar{total !== 1 ? "s" : ""}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
